import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getLinearAssignees, getLinearConnection, getLinearStates, getLinearTeams } from 'features/agents/api/agentsApi';
import ConnectionStatus from 'features/agents/WorkflowEditor/fields/ConnectionStatus';
import { Field, SelectField, TextInput, useIssue } from 'features/agents/WorkflowEditor/fields/inputs';

/** Linear pickers. Each falls back to a plain id field when its list cannot load. */

/** Load a list once per key; `null` key means "nothing to load". */
function useLoaded(load, key, failure) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(key !== null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (key === null) { setRows([]); setError(null); setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const out = await load(key);
        if (!cancelled) setRows(Array.isArray(out) ? out : []);
      } catch (e) {
        if (!cancelled) { setRows([]); setError(e?.response?.data?.error || e?.message || failure); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return { rows, loading, error };
}

const teamKey = (id) => (id == null || id === '' ? null : String(id));

export function LinearTeamPicker({ field, value, onChange }) {
  const { rows: teams, loading, error } = useLoaded(() => getLinearTeams(), 'teams', 'Could not load teams');
  const issue = useIssue(field.key);
  if (!loading && teams.length === 0) {
    return (
      <Field label={field.label} fieldKey={field.key} help={error || 'Connect Linear in Integrations to load teams.'}>
        <TextInput value={value} onChange={onChange} placeholder={error ? 'Paste a Linear team ID' : 'Connect Linear first'} />
      </Field>
    );
  }
  const options = teams.map((t) => ({
    value: t.id,
    label: t.key && t.name ? `${t.key}: ${t.name}` : (t.name || t.key || t.id),
    hint: t.id,
  }));
  return (
    <Field label={field.label} fieldKey={field.key} help={field.help}>
      <SelectField value={value} onChange={onChange} options={options} invalid={!!issue} placeholder={loading ? 'Loading teams' : 'Select team'} />
    </Field>
  );
}

export function LinearStatePicker({ field, value, onChange, linearTeamId }) {
  const teamId = teamKey(linearTeamId);
  const { rows: states, loading, error } = useLoaded(getLinearStates, teamId, 'Could not load states');
  if (!teamId) {
    return (
      <Field label={field.label} fieldKey={field.key}>
        <TextInput value={value} onChange={onChange} placeholder="Choose a Linear team first" />
      </Field>
    );
  }
  if (!loading && states.length === 0) {
    return (
      <Field label={field.label} fieldKey={field.key} help={error || 'No states came back for this team.'}>
        <TextInput value={value} onChange={onChange} placeholder="Paste a workflow state ID" />
      </Field>
    );
  }
  const options = states.map((s) => ({ value: s.id, label: s.type ? `${s.name} (${s.type})` : s.name, hint: s.id }));
  return (
    <Field label={field.label} fieldKey={field.key}>
      <SelectField value={value} onChange={onChange} options={options} placeholder={loading ? 'Loading states' : 'Select state'} />
    </Field>
  );
}

const LINEAR_PRIORITY_OPTIONS = [
  { value: '1', label: 'Urgent' },
  { value: '2', label: 'High' },
  { value: '3', label: 'Medium' },
  { value: '4', label: 'Low' },
  { value: '0', label: 'No priority' },
];

export function LinearPriorityPicker({ field, value, onChange }) {
  const str = value == null || value === '' ? '' : String(value);
  return (
    <Field label={field.label} fieldKey={field.key}>
      <SelectField value={str} onChange={(v) => onChange(v === '' ? null : Number(v))} options={LINEAR_PRIORITY_OPTIONS} placeholder="Select priority" />
    </Field>
  );
}

export function LinearAssigneePicker({ field, value, onChange, linearTeamId }) {
  const teamId = teamKey(linearTeamId);
  const { rows: people, loading, error } = useLoaded(getLinearAssignees, teamId, 'Could not load members');
  const set = (v) => onChange(v || null);
  if (!teamId) {
    return (
      <Field label={field.label} fieldKey={field.key} help="Choose a Linear team to load its members, or paste a user ID.">
        <TextInput value={value} onChange={set} placeholder="Choose a Linear team first" />
      </Field>
    );
  }
  if (!loading && people.length === 0) {
    return (
      <Field label={field.label} fieldKey={field.key} help={error || 'No members came back for this team.'}>
        <TextInput value={value} onChange={set} placeholder="Paste a user ID" />
      </Field>
    );
  }
  const options = [
    { value: '', label: 'No assignee' },
    ...people.map((u) => ({ value: u.id, label: u.email ? `${u.name} (${u.email})` : u.name, hint: u.id })),
  ];
  return (
    <Field label={field.label} fieldKey={field.key}>
      <SelectField value={value} onChange={set} options={options} placeholder={loading ? 'Loading members' : 'Select assignee'} />
    </Field>
  );
}

/** Whether the team's Linear workspace is connected (one workspace per team). */
export function LinearWorkspaceStatus() {
  const { team } = useParams();
  const [conn, setConn] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getLinearConnection()
      .then((c) => { if (!cancelled) setConn(c); })
      .catch(() => { if (!cancelled) setConn({ connected: false }); });
    return () => { cancelled = true; };
  }, []);
  const href = `/${encodeURIComponent(team || '')}/integrations?integration=linear`;
  if (conn === null) return <ConnectionStatus provider="linear" loading title="Checking Linear" />;
  if (!conn.connected) {
    return (
      <ConnectionStatus provider="linear" tone="warning" title="Linear is not connected" action={{ href, label: 'Connect' }}>
        Connect it in Integrations to use this step.
      </ConnectionStatus>
    );
  }
  return (
    <ConnectionStatus provider="linear" title={conn.linear_organization_name || conn.linear_organization_id} action={{ href, label: 'Manage' }}>
      Give the Trodo app access to your teams in Linear settings.
    </ConnectionStatus>
  );
}
